import React from 'react';

import { REGION_ORDER } from '../constants/regions';
import type { AntibodyChainData, AntibodyResponse, SequenceData } from '../types';
import { calcSequenceColumnWidth } from '../utilities/chainWidthUtils';

interface UseGlobalChainWidthsParams {
  clonotypes: AntibodyResponse[];
  showFWR: boolean;
  highlightedRegions: string[];
}

/**
 * Computes heavy and light chain column widths once across ALL clonotypes so
 * every InnerChildSequenceTable gets identical column widths. This prevents
 * proportional browser scaling from misaligning fixed columns (Node, APS, SHMs)
 * between child tables.
 *
 * Uses calcSequenceColumnWidth from chainWidthUtils — single source of truth
 * for the pixel formula shared with the per-clonotype calcChainWidth.
 */
export function useGlobalChainWidths({ clonotypes, showFWR, highlightedRegions }: UseGlobalChainWidthsParams): {
  heavy: number;
  light: number;
} {
  return React.useMemo(() => {
    const activeRegions = highlightedRegions?.length ? highlightedRegions : ['cdr3'];
    const visibleKeys = REGION_ORDER.filter(({ key, isCDR }) => (isCDR ? activeRegions.includes(key) : showFWR && activeRegions.includes(key)));
    const allSequences = clonotypes.flatMap((c) => c.sequences as SequenceData[]);

    const calcWidth = (accessor: (s: SequenceData) => AntibodyChainData | null | undefined) => {
      const chains = allSequences.map(accessor).filter(Boolean) as AntibodyChainData[];
      if (chains.length === 0) {
        return 0;
      }
      const maxLen = Math.max(
        ...chains.map((chain) => {
          if (!chain.regions) {
            return chain.sequence.length;
          }
          return visibleKeys.reduce((total, { key }) => {
            const regionSeq = chain.regions[key as keyof typeof chain.regions];
            return total + (regionSeq?.length ?? 0);
          }, 0);
        }),
      );
      return calcSequenceColumnWidth(maxLen, visibleKeys.length);
    };

    return {
      heavy: calcWidth((seq) => seq.heavyChain),
      light: calcWidth((seq) => seq.lightChain),
    };
  }, [clonotypes, showFWR, highlightedRegions]);
}
